import React from 'react';
import {
    GK_SITUATION_KEYS,
    GK_SITUATION_LABELS,
    GkSituationKey,
    isAllSituations,
} from './insightsGoalkeeperZones';
import {
    collectMatchExclusionPlayerOptions,
    collectMatchExclusionTeamOptions,
    collectMatchExclusionTypeOptions,
    collectMatchShotPlayerOptions,
    collectTeamShooterZoneStats,
    countExclusionKinds,
    mapMatchExclusionsCombined,
    mapTeamLocations,
    mapTeamTargets,
    MatchShotSide,
    shooterCellsToZoneCounts,
    shootPctText,
} from './insightsMatchMapZones';
import { MAP_PERIOD_KEYS, MAP_PERIOD_LABELS, MapPeriodKey } from './insightsMatchStats';
import InsightsMultiSelectDropdown from './InsightsMultiSelectDropdown';
import { MatchBundle } from './insightsQueries';
import {
    ShotFakeKey,
    ShotFoulKey,
    ShotKindFilter,
    shotKindFilterLabel,
    ShotTechniqueKey,
} from './insightsShotKindFilters';
import InsightsShotMap from './InsightsShotMap';
import InsightsSituationMapsShell from './InsightsSituationMapsShell';

interface Props {
    bundles: MatchBundle[];
    ourTeamIds: number[];
    /** Empty-state wording; default "this match", H2H passes "these meetings". */
    contextLabel?: string;
    /** When set, shots-against block names the opponent. */
    opponentName?: string;
}

const emptyBundleFilters = {
    competitionIds: null as number[] | null,
    dateFrom: null as string | null,
    dateTo: null as string | null,
    matchIds: null as number[] | null,
};

const MatchShotBlock: React.FC<{
    bundles: MatchBundle[];
    ourTeamIds: number[];
    side: MatchShotSide;
    title: string;
    hint: string;
}> = ({ bundles, ourTeamIds, side, title, hint }) => {
    const [selected, setSelected] = React.useState<GkSituationKey[]>([...GK_SITUATION_KEYS]);
    const [periodSelected, setPeriodSelected] = React.useState<MapPeriodKey[] | null>(null);
    const [playerSelected, setPlayerSelected] = React.useState<number[] | null>(null);
    const [techniqueSelected, setTechniqueSelected] = React.useState<ShotTechniqueKey[] | null>(
        null
    );
    const [foulSelected, setFoulSelected] = React.useState<ShotFoulKey[] | null>(null);
    const [fakeSelected, setFakeSelected] = React.useState<ShotFakeKey[] | null>(null);
    const [showZoneStats, setShowZoneStats] = React.useState(true);
    const [showShotMarks, setShowShotMarks] = React.useState(true);

    const playerOptions = React.useMemo(
        () =>
            collectMatchShotPlayerOptions(bundles, ourTeamIds, side).map((p) => ({
                value: p.playerId,
                label: p.label,
            })),
        [bundles, ourTeamIds, side]
    );

    const overall = React.useMemo(
        () => collectTeamShooterZoneStats(bundles, ourTeamIds, side),
        [bundles, ourTeamIds, side]
    );

    const kind: ShotKindFilter = React.useMemo(
        () => ({
            techniques: techniqueSelected,
            fouls: foulSelected,
            fakes: fakeSelected,
        }),
        [techniqueSelected, foulSelected, fakeSelected]
    );

    const filtered = React.useMemo(() => {
        const options = {
            situation: selected,
            playerIds: playerSelected,
            periodKeys: periodSelected,
            kind,
        };
        const stats = collectTeamShooterZoneStats(bundles, ourTeamIds, side, options);
        return {
            stats,
            goalMap: mapTeamTargets(bundles, ourTeamIds, side, options),
            poolMap: mapTeamLocations(bundles, ourTeamIds, side, options),
        };
    }, [bundles, ourTeamIds, side, selected, playerSelected, periodSelected, kind]);

    if (overall.shots === 0) return null;

    const situationLabel = isAllSituations(selected)
        ? GK_SITUATION_LABELS.all
        : selected.map((key) => GK_SITUATION_LABELS[key]).join(' + ');
    const kindLabel = shotKindFilterLabel(kind);
    const filterLabel = [situationLabel, kindLabel].filter(Boolean).join(' · ');

    return (
        <InsightsSituationMapsShell
            title={title}
            hint={hint}
            colorMode="shooter"
            selected={selected}
            onSelectedChange={setSelected}
            periodSelected={periodSelected}
            onPeriodSelectedChange={setPeriodSelected}
            showZoneStats={showZoneStats}
            onShowZoneStatsChange={setShowZoneStats}
            showShotMarks={showShotMarks}
            onShowShotMarksChange={setShowShotMarks}
            bundleFilterOptions={{ competitions: [], dates: [], matches: [] }}
            bundleFilters={emptyBundleFilters}
            onBundleFiltersChange={() => undefined}
            hideScopeFilters
            playerOptions={playerOptions}
            playerSelected={playerSelected}
            onPlayerSelectedChange={setPlayerSelected}
            techniqueSelected={techniqueSelected}
            onTechniqueSelectedChange={setTechniqueSelected}
            foulSelected={foulSelected}
            onFoulSelectedChange={setFoulSelected}
            fakeSelected={fakeSelected}
            onFakeSelectedChange={setFakeSelected}
            statsStrip={
                <div className="insightsStatStrip">
                    <div className="insightsStat">
                        <b>{shootPctText(filtered.stats.goals, filtered.stats.shots)}</b>
                        <i>Shooting %</i>
                    </div>
                    <div className="insightsStat">
                        <b>{filtered.stats.shots}</b>
                        <i>Shots</i>
                    </div>
                    <div className="insightsStat">
                        <b>{filtered.stats.goals}</b>
                        <i>Goals</i>
                    </div>
                    <div className="insightsStat">
                        <b>{filtered.stats.misses}</b>
                        <i>Misses</i>
                    </div>
                </div>
            }
            filterSummary={
                filtered.stats.shots > 0 ? (
                    <span>
                        {filterLabel}: {shootPctText(filtered.stats.goals, filtered.stats.shots)} ·{' '}
                        {filtered.stats.goals}G · {filtered.stats.misses}M · {filtered.stats.shots}{' '}
                        Sh
                    </span>
                ) : (
                    <span>{filterLabel}: no shots in this sample</span>
                )
            }
            goalMap={filtered.goalMap}
            poolMap={filtered.poolMap}
            goalZoneCounts={shooterCellsToZoneCounts(filtered.stats.cells)}
            poolZoneCounts={shooterCellsToZoneCounts(filtered.stats.poolCells)}
            goalEmptyHint="No goalmouth targets on these shots."
            poolEmptyHint="No pool locations on these shots."
        />
    );
};

const MatchExclusionBlock: React.FC<{
    bundles: MatchBundle[];
    ourTeamIds: number[];
}> = ({ bundles, ourTeamIds }) => {
    const [playerSelected, setPlayerSelected] = React.useState<number[] | null>(null);
    const [periodSelected, setPeriodSelected] = React.useState<MapPeriodKey[] | null>(null);
    const [typeSelected, setTypeSelected] = React.useState<string[] | null>(null);
    const [teamSelected, setTeamSelected] = React.useState<number[] | null>(null);

    const playerOptions = React.useMemo(
        () =>
            collectMatchExclusionPlayerOptions(bundles, ourTeamIds).map((p) => ({
                value: p.playerId,
                label: p.label,
            })),
        [bundles, ourTeamIds]
    );

    const typeOptions = React.useMemo(() => collectMatchExclusionTypeOptions(bundles), [bundles]);

    const teamOptions = React.useMemo(() => collectMatchExclusionTeamOptions(bundles), [bundles]);

    const allMap = React.useMemo(
        () => mapMatchExclusionsCombined(bundles, ourTeamIds),
        [bundles, ourTeamIds]
    );

    const filteredMap = React.useMemo(
        () =>
            mapMatchExclusionsCombined(bundles, ourTeamIds, {
                playerIds: playerSelected,
                periodKeys: periodSelected,
                typeKeys: typeSelected,
                teamIds: teamSelected,
            }),
        [bundles, ourTeamIds, playerSelected, periodSelected, typeSelected, teamSelected]
    );

    const kinds = countExclusionKinds(filteredMap);

    if (allMap.total === 0) return null;

    return (
        <section className="insightsPanel">
            <div className="insightsPanelHead">
                <h2>Exclusions</h2>
                <span>
                    Full pool · We drew = opp excluded · We got = we excluded · penalty = ring
                </span>
            </div>

            <div className="insightsStatStrip">
                <div className="insightsStat">
                    <b>{kinds.drew}</b>
                    <i>We drew</i>
                </div>
                <div className="insightsStat">
                    <b>{kinds.got}</b>
                    <i>We got</i>
                </div>
                <div className="insightsStat">
                    <b>{kinds.penalty}</b>
                    <i>Penalties</i>
                </div>
                <div className="insightsStat">
                    <b>{filteredMap.total}</b>
                    <i>Total</i>
                </div>
            </div>

            <div className="insightsMapToolbar">
                <div className="insightsMapToolbarFilters">
                    <InsightsMultiSelectDropdown
                        label="Period"
                        options={MAP_PERIOD_KEYS.map((key) => ({
                            value: key,
                            label: MAP_PERIOD_LABELS[key],
                        }))}
                        selected={periodSelected}
                        onChange={setPeriodSelected}
                    />
                    {typeOptions.length > 0 ? (
                        <InsightsMultiSelectDropdown
                            label="Type"
                            options={typeOptions}
                            selected={typeSelected}
                            onChange={setTypeSelected}
                        />
                    ) : null}
                    {teamOptions.length > 0 ? (
                        <InsightsMultiSelectDropdown
                            label="Team"
                            options={teamOptions}
                            selected={teamSelected}
                            onChange={setTeamSelected}
                        />
                    ) : null}
                    {playerOptions.length > 0 ? (
                        <InsightsMultiSelectDropdown
                            label="Player"
                            options={playerOptions}
                            selected={playerSelected}
                            onChange={setPlayerSelected}
                            listClassName="insightsMultiSelectListTall"
                        />
                    ) : null}
                </div>
            </div>

            <div className="insightsMapStack">
                <InsightsShotMap
                    side={filteredMap}
                    title="Pool location"
                    surface="pool"
                    fullBleed
                    emptyHint="No exclusion locations in this filter."
                />
            </div>
        </section>
    );
};

/**
 * Match-page maps: player-style shooting blocks (boxes, situation + player filters,
 * zoomed half-pool, goal/miss) plus one combined full-pool exclusion map.
 */
const InsightsMatchMaps: React.FC<Props> = ({
    bundles,
    ourTeamIds,
    contextLabel = 'this match',
    opponentName,
}) => {
    const hasShotsFor = collectTeamShooterZoneStats(bundles, ourTeamIds, 'for').shots > 0;
    const hasShotsAgainst = collectTeamShooterZoneStats(bundles, ourTeamIds, 'against').shots > 0;
    const hasExclusions = mapMatchExclusionsCombined(bundles, ourTeamIds).total > 0;

    if (!hasShotsFor && !hasShotsAgainst && !hasExclusions) {
        return (
            <section className="insightsPanel">
                <div className="insightsPanelHead">
                    <h2>Maps</h2>
                    <span>Shots and exclusions</span>
                </div>
                <div className="insightsEmpty">
                    No shots or exclusions in {contextLabel}.
                </div>
            </section>
        );
    }

    return (
        <>
            <MatchShotBlock
                bundles={bundles}
                ourTeamIds={ourTeamIds}
                side="for"
                title="Our shots"
                hint="Shooting % = goals / shots · green = goal · red = miss"
            />
            <MatchShotBlock
                bundles={bundles}
                ourTeamIds={ourTeamIds}
                side="against"
                title={opponentName ? `Shots against · ${opponentName}` : 'Shots against'}
                hint="Opponent shooting · green = goal · red = miss"
            />
            <MatchExclusionBlock bundles={bundles} ourTeamIds={ourTeamIds} />
        </>
    );
};

export default InsightsMatchMaps;
